import React, { createContext, useCallback, useState } from 'react';
import './ToastContext.css';

export type ToastSeverity = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  message: string;
  severity: ToastSeverity;
  duration?: number;
  title?: string;
}

interface ToastContextType {
  showToast: (message: string, severity: ToastSeverity, duration?: number, title?: string) => void;
  hideToast: (id: string) => void;
}

export const ToastContext = createContext<ToastContextType | undefined>(undefined);

interface ToastProviderProps {
  children: React.ReactNode;
}

const getIcon = (severity: ToastSeverity) => {
  const iconMap = {
    success: '✓',
    error: '✕',
    warning: '!',
    info: 'ⓘ',
  };
  return iconMap[severity];
};

const ToastItem: React.FC<{
  toast: Toast;
  onClose: (id: string) => void;
  onRemove: (id: string) => void;
}> = ({ toast, onClose, onRemove }) => {
  const [progress, setProgress] = useState(100);
  const duration = toast.duration || 4000;

  React.useEffect(() => {
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const newProgress = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(newProgress);

      if (newProgress <= 0) {
        clearInterval(interval);
        onRemove(toast.id);
      }
    }, 10);

    return () => clearInterval(interval);
  }, [duration, toast.id, onRemove]);

  return (
    <div className={`toast toast--${toast.severity} toast--animate-in`}>
      <div className="toast__header">
        <div className="toast__icon-title">
          <span className={`toast__icon toast__icon--${toast.severity}`}>
            {getIcon(toast.severity)}
          </span>
          {toast.title && <span className="toast__title">{toast.title}</span>}
        </div>
        <button
          className="toast__close"
          onClick={() => onClose(toast.id)}
          aria-label="Close toast"
        >
          ✕
        </button>
      </div>
      <div className="toast__content">
        <p className="toast__message">{toast.message}</p>
      </div>
      <div className="toast__progress" style={{ width: `${progress}%` }} />
    </div>
  );
};

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback(
    (message: string, severity: ToastSeverity, duration = 4000, title?: string) => {
      const id = `${Date.now()}-${Math.random()}`;
      const newToast: Toast = { id, message, severity, duration, title };
      setToasts((prev) => [...prev, newToast]);
    },
    []
  );

  const hideToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, hideToast }}>
      {children}
      <div className="toast-container">
        {toasts.map((toast) => (
          <ToastItem
            key={toast.id}
            toast={toast}
            onClose={hideToast}
            onRemove={hideToast}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
};
